'use client';
import {Box} from '@mui/material';
import {styled, type Theme} from '@mui/material/styles';
import dynamic from 'next/dynamic';
import {type FC, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {AppIcon, FaFullscreenIcon} from '../../icon';
import {outsideArrowMinViewport} from '../../listing/carouselArrowSx';
import {FullscreenButton, NavArrow, SectionTitle} from '../../primitives';
import type {MediaAsset} from '../types';

// Code-split the full-screen lightbox — only fetched when a viewer opens it.
const GalleryModal = dynamic(() => import('../GalleryModal').then(m => m.GalleryModal), {ssr: false});

// Inline frame height (Figma 645:38593): a bordered white card holding one
// contained floor-plan image with a corner fullscreen affordance.
const FRAME_HEIGHT = 280;
const FRAME_HEIGHT_MD = 420;

const Root = styled('section')(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

// Below md, arrows flank the card in-flow (this viewer is index-based with no
// swipe track, so mobile needs them too). From md up they float outside the
// Container via arrowSx, so the card alone fills the row.
const Row = styled(Box)(({theme}) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

const ARROW_TRACK_GAP = 3;

// Arrows float outside the card only once the page gutter can actually hold them —
// gating on `md` pushed them off-screen and scrolled the document horizontally all
// the way to 1223px. Below that they stay in-flow beside the card, which this viewer
// needs anyway: it is index-based with no swipe track, so the arrows are the only
// way to page through. Logical inset/margin keep RTL correct.
const arrowSx = (dir: 'prev' | 'next') => (theme: Theme) => ({
  [`@media (min-width: ${outsideArrowMinViewport(theme, ARROW_TRACK_GAP)}px)`]: {
    position: 'absolute' as const,
    top: '50%',
    transform: 'translateY(-50%)',
    ...(dir === 'prev'
      ? {insetInlineEnd: '100%', marginInlineEnd: theme.spacing(ARROW_TRACK_GAP)}
      : {insetInlineStart: '100%', marginInlineStart: theme.spacing(ARROW_TRACK_GAP)}),
  },
});

const Frame = styled(Box)(({theme}) => ({
  position: 'relative',
  flex: 1,
  minWidth: 0,
  height: FRAME_HEIGHT,
  borderRadius: theme.radii.images,
  border: `1px solid ${theme.palette.colors.border.neutral.subtle}`,
  background: theme.palette.colors.surface.neutral.default,
  overflow: 'hidden',
  [theme.breakpoints.up('md')]: {height: FRAME_HEIGHT_MD},
}));

// The image itself is a button so a click anywhere on the plan opens the modal.
const ImageButton = styled('button')(({theme}) => ({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  padding: theme.spacing(3),
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:focus-visible': {outline: `2px solid ${theme.palette.colors.border.focus.default}`, outlineOffset: -2},
}));

const PlanImage = styled('img')({
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
});

// Pins the shared FullscreenButton chrome to the frame's corner.
const fullscreenButtonSx = (theme: Theme) => ({
  position: 'absolute' as const,
  insetInlineEnd: theme.spacing(2),
  bottom: theme.spacing(2),
});

export interface FloorPlanViewerProps {
  images: MediaAsset[];
  title?: string;
  onOpen?: () => void;
}

export const FloorPlanViewer = function FloorPlanViewer({images, title, onOpen}: FloorPlanViewerProps) {
  const {t} = useTranslation();
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);

  if (images.length === 0) {
    return null;
  }

  const heading = title ?? t('floor_plan');
  const hasMany = images.length > 1;
  const active = images[index];
  const openModal = () => {
    onOpen?.();
    setOpen(true);
  };

  return (
    <Root>
      <SectionTitle as="h3">{heading}</SectionTitle>

      <Row>
        {hasMany && (
          <NavArrow
            direction="prev"
            disabled={index === 0}
            onClick={() => {
              setIndex(current => Math.max(0, current - 1));
            }}
            sx={arrowSx('prev')}
          />
        )}

        <Frame>
          <ImageButton type="button" onClick={openModal}>
            <PlanImage src={active.src} alt={active.alt ?? heading} />
          </ImageButton>
          <FullscreenButton onClick={openModal} sx={fullscreenButtonSx}>
            <AppIcon icon={FaFullscreenIcon} aria-hidden />
          </FullscreenButton>
        </Frame>

        {hasMany && (
          <NavArrow
            direction="next"
            disabled={index === images.length - 1}
            onClick={() => {
              setIndex(current => Math.min(images.length - 1, current + 1));
            }}
            sx={arrowSx('next')}
          />
        )}
      </Row>

      {open && (
        <GalleryModal
          assets={images}
          index={index}
          title={heading}
          onIndexChange={setIndex}
          onClose={() => {
            setOpen(false);
          }}
        />
      )}
    </Root>
  );
} as FC<FloorPlanViewerProps>;

FloorPlanViewer.displayName = 'FloorPlanViewer';
